import Head from "next/head";

export default function Layout({ title, description, image, ogPath, ogType = "website", left, leftMobile = true, center, right, hideTitle = false, children }) {
    const SITE_URL =
        process.env.NEXT_PUBLIC_SITE_URL ||
        process.env.NEXT_PUBLIC_FRONTEND_URL ||
        process.env.NEXT_PUBLIC_STRAPI_URL ||
        "";

    const APP_NAME = process.env.NEXT_PUBLIC_APP_NAME || "";

    const toAbsoluteUrl = (u) => {
        if (!u) return "";
        if (u.startsWith("http://") || u.startsWith("https://")) return u;
        if (!SITE_URL) return u;
        return `${SITE_URL}${u.startsWith("/") ? "" : "/"}${u}`;
    };

    const absImage = toAbsoluteUrl(image);

    // safety: ha valahonnan mégis HTML/entity jönne
    const stripHtmlForMeta = (input) => {
        const s = (input ?? "").toString();
        const noTags = s.replace(/<[^>]*>/g, " ");
        const decoded = noTags
            .replace(/&nbsp;|&#160;/gi, " ")
            .replace(/&amp;/gi, "&")
            .replace(/&quot;/gi, '"')
            .replace(/&#39;|&apos;/gi, "'")
            .replace(/&lt;/gi, "<")
            .replace(/&gt;/gi, ">");
        return decoded.replace(/\s+/g, " ").trim();
    };

    const ellipsis = (s, max) => {
        const str = stripHtmlForMeta(s);
        if (!str) return "";
        if (str.length <= max) return str;
        return str.slice(0, Math.max(0, max - 3)).trimEnd() + "...";
    };

    // OG / TW limits
    const ogTitle = ellipsis(title, 40);
    const ogDesc = ellipsis(description, 140);

    const twTitle = ogTitle;
    const twDesc = ogDesc;

    // basic SEO mezők is ezeket kapják (ne a nyerset)
    const pageTitle = ogTitle || APP_NAME;
    const pageDesc = ogDesc;

    const absOgUrl = toAbsoluteUrl(ogPath);

    const FB_APP_ID = process.env.NEXT_PUBLIC_FB_APP_ID;

    const hasTitle = !!title?.trim?.() && !hideTitle;

    const minHeightClass = hasTitle
        ? "min-h-[calc(100dvh-154px)]"
        : "min-h-[calc(100dvh-80px)]";

    return (
        <>
            <Head>
                {/* BASIC SEO */}
                <title>{pageTitle}</title>
                {pageDesc && <meta name="description" content={pageDesc} />}

                {/* OG */}
                <meta property="og:title" content={ogTitle} />
                {ogDesc && <meta property="og:description" content={ogDesc} />}
                <meta property="og:type" content={ogType || "website"} />
                {absImage && <meta property="og:image" content={absImage} />}
                {FB_APP_ID && <meta property="fb:app_id" content={FB_APP_ID} />}

                {absOgUrl && (
                    <>
                        <meta property="og:url" content={absOgUrl} />
                        <link rel="canonical" href={absOgUrl} />
                    </>
                )}

                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={twTitle} />
                {twDesc && <meta name="twitter:description" content={twDesc} />}
                {absImage && <meta name="twitter:image" content={absImage} />}
            </Head>

            <div className="text-black dark:text-white mx-auto mt-2 mb-2">

                {children}

                <div className="mx-auto md:px-3">
                    {/* TITLE BOX */}
                    {title?.trim?.() && !hideTitle && (
                        <div
                            className="flex flex-wrap justify-between items-center gap-4 px-3 py-4
               bg-white dark:bg-zinc-900 shadow-sm rounded-xl
               dark:border border-gray-100 dark:border-zinc-700 mb-3"
                        >
                            <div className="flex min-w-0">
                                <div className="w-1 bg-black dark:bg-white"></div>

                                <div className="border-l-2 border-black dark:border-white pl-2 min-w-0">
                                    <div className="text-xl font-semibold text-gray-900 dark:text-gray-100 truncate">
                                        {title}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* MAIN CONTENT */}
                    <div className={`p-2 space-y-4 bg-white dark:bg-zinc-900 shadow-sm rounded-xl 
                                dark:border border-gray-100 dark:border-zinc-700 ${minHeightClass}`}>

                        <div className="flex flex-col lg:flex-row lg:items-start gap-4 justify-center">

                            {/* LEFT COLUMN */}
                            {left && (
                                <div
                                    className={`
        ${leftMobile ? "block" : "hidden"}
        lg:block
        w-full lg:w-[400px]
        shrink-0
        space-y-4
    `}
                                >
                                    {left}
                                </div>
                            )}

                            {/* CENTER COLUMN */}
                            <div className="flex-grow min-w-0 space-y-4">

                                {center}

                            </div>

                            {/* RIGHT COLUMN */}
                            {right && (
                                <div className="hidden 2xl:block w-full lg:w-[400px] shrink-0 space-y-4">

                                    {right}

                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}